<template>
  <div class="section available-tutors-section">
    <div class="available-tutors-container">
      <div class="section-header">
        <h2 class="section-title available-tutors-title">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          Available Tutors
        </h2>
        <button class="refresh-btn" @click="$emit('refresh')">
          <ArrowPathIcon class="w-4 h-4" />
          Refresh
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="tutors-grid available-grid">
        <div v-for="i in 4" :key="'loading-' + i" class="tutor-card animate-pulse">
          <div class="tutor-card-header">
            <div class="tutor-avatar skeleton"></div>
            <div class="tutor-name-rating">
              <div class="skeleton-line"></div>
              <div class="skeleton-line short"></div>
            </div>
          </div>
          <div class="tutor-info">
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line short"></div>
          </div>
        </div>
      </div>

      <!-- No Tutors State -->
      <div v-else-if="tutors.length === 0" class="text-center py-12">
        <div
          class="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4"
        >
          <svg
            class="w-12 h-12 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No Available Tutors Found</h3>
        <p class="text-gray-500 max-w-md mx-auto">
          There are currently no available tutors. Please check back later.
        </p>
      </div>

      <!-- Tutors Grid -->
      <div v-else class="tutors-grid available-grid">
        <TutorCard
          v-for="tutor in tutors"
          :key="tutor.id"
          :tutor="tutor"
          :canBookSessions="canBookSessions"
          :canMessage="canMessage"
          :hasBlockingPendingPostTest="hasBlockingPendingPostTest"
          @openProfile="$emit('openProfile', tutor)"
          @sendMessage="$emit('sendMessage', tutor)"
          @bookSession="$emit('bookSession', tutor)"
        />
      </div>

      <!-- Pagination -->
      <TutorPagination
        v-if="!isLoading && tutors.length > 0"
        :currentPage="currentPage"
        :totalPages="totalPages"
        :itemsPerPage="itemsPerPage"
        :totalItems="totalItems"
        @pageChange="$emit('pageChange', $event)"
        @itemsPerPageChange="$emit('itemsPerPageChange', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import TutorCard from "./TutorCard.vue";
import TutorPagination from "./TutorPagination.vue";
import { ArrowPathIcon } from "@heroicons/vue/24/outline";

defineProps({
  tutors: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  totalPages: {
    type: Number,
    default: 1,
  },
  itemsPerPage: {
    type: Number,
    default: 4,
  },
  totalItems: {
    type: Number,
    default: 0,
  },
  canBookSessions: {
    type: Boolean,
    default: true,
  },
  canMessage: {
    type: Boolean,
    default: true,
  },
  hasBlockingPendingPostTest: {
    type: Boolean,
    default: false,
  },
});

defineEmits([
  "openProfile",
  "sendMessage",
  "bookSession",
  "refresh",
  "pageChange",
  "itemsPerPageChange",
]);
</script>

<style scoped>
.section {
  max-width: 1150px;
  margin: 0 auto 32px;
  padding: 0 20px;
  width: 100%;
}

.available-tutors-section {
  margin-bottom: 50px;
  padding: 0 20px;
  width: 100%;
}

.available-tutors-container {
  background: #fdfdfd;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.2s;
  margin: 0 auto;
  width: 100%;
  border: 3px solid #000000;
  border-radius: 12px;
  box-shadow: 2px 2px 0px #000;
}

.available-tutors-container:hover {
  transform: translateY(-1px);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  width: 100%;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  color: #193cb8;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.available-tutors-title {
  font-size: 22px !important;
  font-weight: 800 !important;
  color: #193cb8 !important;
}

.refresh-btn {
  padding: 6px 12px;
  background: white;
  border: 2px solid #016630;
  border-radius: 12px;
  color: #016630;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  box-shadow: 2px 2px 0px #016630;
}

.refresh-btn:hover {
  transform: translateY(-1px);
  box-shadow: 3px 3px 0px #016630;
  color: #016630;
}

.refresh-icon {
  color: currentColor;
  stroke-width: 2.5;
}

.tutors-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 16px;
  width: 100%;
  overflow: visible;
}

/* Loading States */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.skeleton {
  background-color: #e2e8f0;
}

.skeleton-line {
  height: 16px;
  background-color: #e2e8f0;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-line.short {
  width: 60%;
}

/* No tutors state */
.text-center {
  text-align: center;
}

.py-12 {
  padding: 3rem 0;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.w-24 {
  width: 6rem;
}

.h-24 {
  height: 6rem;
}

.bg-gray-100 {
  background-color: #f3f4f6;
}

.rounded-full {
  border-radius: 9999px;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-center {
  justify-content: center;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.w-12 {
  width: 3rem;
}

.h-12 {
  height: 3rem;
}

.text-gray-400 {
  color: #9ca3af;
}

.text-lg {
  font-size: 1.125rem;
}

.font-medium {
  font-weight: 500;
}

.text-gray-900 {
  color: #111827;
}

.text-gray-500 {
  color: #6b7280;
}

.max-w-md {
  max-width: 28rem;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .tutors-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .section-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>
